// WebSocket 连接管理器（浏览器原生 WebSocket 版）。
// 对应小程序侧 utils/websocketManager.js（2026-07 后版本）：
// - 同一 URL 建连期间共享一个 Promise，避免重复创建 socket；
// - 旧 socket 的迟到回调（onclose 等）不得删除同 URL 的新连接；
// - socket 尚未创建完成时收到关闭请求（pendingCloseUrls），创建后立即关闭。
const WebSocketManager = {
    // 存储所有活跃的 WebSocket 连接
    connections: {},
    // 同一 URL 建连期间共享一个 Promise，避免重复创建 socket
    pendingConnections: {},
    // 记录 socket 尚未创建完成时收到的关闭请求
    pendingCloseUrls: new Set(),

    // 获取现有连接
    getExistingConnection(url) {
        try {
            const connection = this.connections[url];
            if (connection && connection.readyState === WebSocket.OPEN) {
                return connection;
            }
            return null;
        } catch (error) {
            console.error('获取现有连接失败:', url, error);
            return null;
        }
    },

    // 获取连接（如果不存在则创建）
    getConnection(url, options = {}) {
        // 如果已存在相同 URL 的连接，直接返回
        const existingConnection = this.getExistingConnection(url);
        if (existingConnection) {
            return Promise.resolve(existingConnection);
        }

        const pendingConnection = this.pendingConnections[url];
        if (pendingConnection) {
            const connectingSocket = this.connections[url];
            if (!connectingSocket || connectingSocket.readyState === WebSocket.CONNECTING) {
                return pendingConnection;
            }
            // 旧连接已经进入 closing/closed，不能继续复用它的建连 Promise
            delete this.pendingConnections[url];
        }

        // 检查连接数量限制
        let connectionPromise;
        const clearPendingConnection = () => {
            if (this.pendingConnections[url] === connectionPromise) {
                delete this.pendingConnections[url];
            }
        };

        connectionPromise = this.checkSocketLimit().then(() => {
            // 等待连接限制检查时，其他调用可能已经建立了同 URL 连接
            const connectionAfterCheck = this.getExistingConnection(url);
            if (connectionAfterCheck) {
                clearPendingConnection();
                if (this.pendingCloseUrls.has(url)) {
                    this.closeConnection(url);
                }
                return connectionAfterCheck;
            }

            // 创建新连接
            const socketTask = new WebSocket(url);
            let everOpened = false;

            // 连接打开事件
            socketTask.onopen = () => {
                everOpened = true;
                clearPendingConnection();
                if (options.onOpen) options.onOpen();
            };

            // 连接关闭事件
            socketTask.onclose = (res) => {
                // 旧 socket 的迟到回调不能删除同 URL 的新连接
                if (this.connections[url] === socketTask) {
                    delete this.connections[url];
                }
                clearPendingConnection();
                this.pendingCloseUrls.delete(url);
                if (options.onClose) options.onClose(res);
            };

            // 连接错误：从未打开过视为建连失败（onFail），否则视为连接期错误（onError）
            socketTask.onerror = (err) => {
                if (!everOpened) {
                    if (options.onFail) options.onFail(err);
                    return;
                }
                if (options.onError) options.onError(err);
            };

            socketTask.onmessage = (res) => {
                if (options.onMessage) options.onMessage(res);
            };

            // 存储连接
            this.connections[url] = socketTask;

            // 建连期间已请求关闭：创建完成后立即关闭
            if (this.pendingCloseUrls.delete(url)) {
                this.closeConnection(url);
            }
            return socketTask;
        });

        this.pendingConnections[url] = connectionPromise;
        connectionPromise.catch(() => {
            clearPendingConnection();
            this.pendingCloseUrls.delete(url);
        });
        return connectionPromise;
    },

    // 关闭指定连接
    closeConnection(url) {
        const connection = this.connections[url];
        if (!connection) {
            if (this.pendingConnections[url]) {
                this.pendingCloseUrls.add(url);
            }
            return;
        }

        this.pendingCloseUrls.delete(url);
        try {
            connection.close();
        } catch (err) {
            console.error('关闭 WebSocket 连接失败:', url, err);
            if (this.connections[url] === connection) {
                delete this.connections[url];
            }
        }
    },

    // 关闭所有连接
    closeAllConnections() {
        const urls = new Set([
            ...Object.keys(this.connections),
            ...Object.keys(this.pendingConnections),
        ]);
        urls.forEach((url) => {
            this.closeConnection(url);
        });
    },

    // 检查连接数量限制
    checkSocketLimit() {
        return new Promise((resolve) => {
            // 获取当前连接数量
            const connectionCount = Object.keys(this.connections).length;

            // 如果连接数量已达到上限，关闭最早的连接
            if (connectionCount >= 5) {
                console.log('WebSocket 连接数量已达上限，关闭最早的连接');
                const oldestUrl = Object.keys(this.connections)[0];
                this.closeConnection(oldestUrl);
            }

            resolve();
        });
    }
};

export default WebSocketManager;
